import { Link } from 'react-router-dom';
import { ArrowUpRight, Instagram, Facebook, Linkedin, Plus } from 'lucide-react';
import { CONTACT_INFO, SOCIAL_LINKS } from '@/data/contactInfo';

// Inline SVG brand icons not in lucide-react
const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.86A8.16 8.16 0 0 0 21.5 10.4V7a4.85 4.85 0 0 1-1.91-.31z" />
  </svg>
);

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
  </svg>
);

const ICON_MAP = {
  LinkedIn: Linkedin,
  Instagram: Instagram,
  Facebook: Facebook,
  TikTok: TikTokIcon,
  WhatsApp: WhatsAppIcon,
};

export const Footer = () => {
  return (
    <footer
      className="relative border-t border-white/10 overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/assets/backgrounds/contact-bg.jpg')", backgroundColor: '#0F0F0F' }}
      data-testid="site-footer"
    >
      <div
        className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(255,107,0,0.45), transparent 70%)' }}
      />

      <div className="relative section-pad" style={{ paddingLeft: '7%', paddingRight: '7%' }}>
        {/* ======= TOP ROW ======= */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mb-14 lg:mb-20">
          {/* LEFT: contact details (-5pt typography) */}
          <div className="flex flex-col gap-3">
            <a
              href={`mailto:${CONTACT_INFO.email}`}
              className="text-[var(--brand-orange)] text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight hover:text-white transition-colors break-all leading-tight"
              data-testid="footer-email-link"
            >
              {CONTACT_INFO.email}
            </a>
            <a
              href={`tel:${CONTACT_INFO.phoneE164}`}
              className="text-white text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight hover:text-[var(--brand-orange)] transition-colors leading-tight"
              data-testid="footer-phone-link"
            >
              {CONTACT_INFO.phone}
            </a>
          </div>

          {/* RIGHT: tagline + button — LEFT-aligned */}
          <div className="flex flex-col gap-6 items-start">
            <p className="text-white/85 text-base sm:text-lg lg:text-[20px] leading-snug max-w-2xl text-left">
              {CONTACT_INFO.tagline}
            </p>
            <Link
              to="/contacto#contactanos"
              className="group inline-flex items-center gap-2 bg-white text-[var(--brand-orange)] hover:text-[#58677A] rounded-full pl-6 pr-2 py-2 font-medium text-sm hover:-translate-y-0.5 transition-all duration-200 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
              data-testid="footer-cta-button"
            >
              <span>Empecemos a trabajar juntos</span>
              <span className="w-8 h-8 rounded-full bg-[var(--brand-orange)] group-hover:bg-black text-white flex items-center justify-center transition-colors duration-200">
                <Plus size={16} strokeWidth={2.5} />
              </span>
            </Link>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/15 mb-14 lg:mb-16" />

        {/* ======= BOTTOM ROW (same 2-col grid as top row so nav aligns with tagline) ======= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* LEFT: Logo */}
          <div>
            <img
              src="/assets/logos/linkeate-con-link-blanco.png"
              alt="Linkéate con Link"
              className="h-24 sm:h-28 lg:h-36 w-auto"
            />
          </div>

          {/* RIGHT: Nav + Locations split inside */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
            {/* Nav (aligned to the same x-position as the tagline above) */}
            <nav className="flex flex-col gap-4 text-base text-white/80">
              <Link to="/nosotros" className="hover:text-white transition">Nosotros</Link>
              <Link to="/servicios-proyectos" className="hover:text-white transition">Servicios</Link>
              <Link to="/servicios-proyectos" className="hover:text-white transition">Proyectos</Link>
              <Link to="/contacto" className="hover:text-white transition">Contacto</Link>
              <Link
                to="/aviso-privacidad"
                className="hover:text-white transition"
                data-testid="footer-privacy-link"
              >
                Aviso de Privacidad
              </Link>
            </nav>

            {/* Locations + socials */}
            <div className="flex flex-col gap-5">
              <ul className="flex flex-col gap-3 text-base text-white/85">
                {CONTACT_INFO.locations.map((loc) => (
                  <li key={loc.city}>{loc.city}</li>
                ))}
                <li className="text-white/65">Ecuador</li>
              </ul>

              {/* Social icons */}
              <div className="flex items-center gap-3 pt-1">
                {SOCIAL_LINKS.map((s) => {
                  const Icon = ICON_MAP[s.name];
                  return (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      data-testid={`footer-social-${s.name.toLowerCase()}`}
                      className="w-[34px] h-[34px] rounded-full bg-white text-[#58677A] flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:bg-[var(--brand-orange)] hover:text-white"
                    >
                      {Icon ? <Icon size={15} /> : null}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Credits */}
        <div className="mt-14 lg:mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/55 flex flex-wrap items-center gap-2">
            <span>© {new Date().getFullYear()} Link. Todos los derechos reservados.</span>
            <span className="text-white/30">|</span>
            <a
              href="https://www.emprenovus.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-[var(--brand-orange)] transition-colors"
              data-testid="footer-emprenovus-link"
            >
              <img src="/assets/icons/emprenovus.png" alt="Emprenovus" className="w-4 h-4 object-contain" />
              AI powered by Emprenovus
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
